<div class="report-incident-modal" *transloco="let t; prefix: 'accidents.modals.reportIncident'">
  <div class="report-incident-modal__header">
    <h3 class="report-incident-modal__title">{{ t('title') }}</h3>
    <button type="button" class="icon-btn" [attr.aria-label]="'common.close' | transloco" (click)="cancel()" [disabled]="isSubmitting">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M18 6L6 18M6 6l12 12"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
        </path>
      </svg>
    </button>
  </div>

  <div class="step-label">
    @if (currentStep === reportIncidentStep.BasicInformation) {
      <span>{{ t('stepBasicInformation') }}</span>
    } @else {
      <span>{{ t('stepPeopleInvolved') }}</span>
    }
  </div>

  <div class="tab-indicator">
    <div class="tab-indicator__line" [class.active]="currentStep === reportIncidentStep.BasicInformation"></div>
    <div class="tab-indicator__line" [class.active]="currentStep === reportIncidentStep.PeopleInvolved"></div>
  </div>

  <!-- Both steps stay mounted so the location input keeps its Places autocomplete after Back -->
  <form [formGroup]="reportForm" class="report-incident-modal__form">
    <div class="form-step" [hidden]="currentStep !== reportIncidentStep.BasicInformation">
      <div class="form-group">
        <app-asset-search-dropdown
          [accountIds]="accountIds"
          [selectedAssetId]="reportForm.get('assetId')?.value"
          [selectedAssetName]="selectedAsset?.assetName ?? ''"
          [showRequiredError]="!!(reportForm.get('assetId')?.invalid && reportForm.get('assetId')?.touched)"
          (assetSelected)="onAssetSelect($event)">
        </app-asset-search-dropdown>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">{{ t('incidentType') }} <span class="required">*</span></label>
          <div ngbDropdown class="dropdown-wrapper" [autoClose]="true">
            <button type="button" class="form-control dropdown-toggle" ngbDropdownToggle>
              <div class="dropdown-inner">
                <span [class.dropdown-placeholder]="!selectedIncidentType">{{ incidentTypeLabel }}</span>
                <i class="ph ph-caret-down"></i>
              </div>
            </button>
            <div ngbDropdownMenu class="dropdown-menu">
              @if (isLoadingIncidentTypes) {
                <div class="dropdown-loading">
                  <span class="spinner-border spinner-border-sm"></span>
                </div>
              } @else if (!incidentTypes.length) {
                <div class="dropdown-empty">{{ t('noIncidentTypesFound') }}</div>
              } @else {
                @for (type of incidentTypes; track type.id) {
                  <button type="button" class="dropdown-item" (click)="onIncidentTypeSelect(type)" ngbDropdownItem>
                    {{ type.localizedName || type.name }}
                  </button>
                }
              }
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ t('severity') }} <span class="required">*</span></label>
          <div ngbDropdown class="dropdown-wrapper" [autoClose]="true">
            <button type="button" class="form-control dropdown-toggle" ngbDropdownToggle>
              <div class="dropdown-inner">
                <span [class.dropdown-placeholder]="!selectedSeverity">{{ severityLabel }}</span>
                <i class="ph ph-caret-down"></i>
              </div>
            </button>
            <div ngbDropdownMenu class="dropdown-menu">
              @for (severity of severities; track severity) {
                <button type="button" class="dropdown-item" (click)="onSeveritySelect(severity)" ngbDropdownItem>
                  {{ severityLabelKeys[severity] | transloco }}
                </button>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="form-row">
        <app-flatpickr-date-picker
          [label]="t('dateAndTime')"
          [(value)]="dateTime"
          [disabled]="false"
          [enableTime]="true"
          dateFormat="Y-m-d H:i"
          maxDate="today"
          [allowPastDates]="true"
          [required]="true">
        </app-flatpickr-date-picker>

        <div class="form-group">
          <label class="form-label">{{ t('location') }} <span class="required">*</span></label>
          <div class="input-with-icon">
            <i class="ph ph-magnifying-glass"></i>
            <input
              #locationInput
              type="text"
              formControlName="location"
              class="form-control"
              autocomplete="off"
              [title]="reportForm.get('location')?.value || ''"
              [placeholder]="t('locationPlaceholder')">
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('description') }} <span class="required">*</span></label>
        <textarea
          formControlName="description"
          class="form-control textarea"
          [placeholder]="t('descriptionPlaceholder')"
          rows="4">
        </textarea>
      </div>
    </div>

    <div class="form-step" [hidden]="currentStep !== reportIncidentStep.PeopleInvolved">
      <div class="form-group">
        <label class="form-label">{{ t('driverInvolved') }} <span class="required">*</span></label>
        <input
          type="text"
          formControlName="driverInvolved"
          class="form-control"
          autocomplete="off"
          [placeholder]="t('driverInvolvedPlaceholder')">
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('witnessDetails') }}</label>
        <textarea
          [(ngModel)]="witnessDetails"
          [ngModelOptions]="{standalone: true}"
          class="form-control textarea"
          [placeholder]="t('witnessDetailsPlaceholder')"
          rows="4">
        </textarea>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('attachPhotosOrVideos') }}</label>
        <div
          class="file-upload-area"
          [class.drag-over]="isDragOver"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          (click)="fileInput.click()">
          <input
            type="file"
            #fileInput
            multiple
            accept="image/*,video/*"
            (change)="onFileSelect($event)"
            style="display: none;">
          <span class="file-upload-text">{{ t('dragAndDrop') }}</span>
        </div>

        @if (attachmentError) {
          <span class="attachment-error">{{ attachmentError }}</span>
        }

        @if (attachedFiles.length > 0) {
          <div class="upload-status-list">
            @for (file of attachedFiles; track file.reference; let i = $index) {
              <div class="upload-status">
                <div class="upload-status-info-icon">
                  <i class="ph ph-file"></i>
                </div>

                <div class="upload-status-info-text">
                  <span class="upload-status-file-name" [title]="file.name">{{ file.name }}</span>
                  @if (file.isUploading) {
                    <div class="upload-status-progress-bar-container">
                      <div class="upload-status-progress-bar" [style.width.%]="file.uploadProgress"></div>
                    </div>
                  } @else if (file.uploadFailed) {
                    <span class="upload-status-failed">{{ t('uploadFailed') }}</span>
                  } @else if (file.uploadSuccess) {
                    <span class="upload-status-success">{{ t('uploadSuccess') }}</span>
                  }
                </div>

                @if (file.isUploading) {
                  <div class="upload-status-percentage">
                    <span>{{ file.uploadProgress }}%</span>
                  </div>
                }

                <button type="button" class="upload-status-delete-file-btn" (click)="removeFile(i); $event.stopPropagation()">
                  <i class="ph ph-x"></i>
                </button>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </form>

  <div class="report-incident-modal__actions">
    @if (currentStep === reportIncidentStep.BasicInformation) {
      <button type="button" class="btn-cancel" (click)="cancel()">
        <span>{{ 'common.cancel' | transloco }}</span>
      </button>
      <button type="button" class="btn-next" (click)="nextStep()" [disabled]="!isStep1Valid">
        <span>{{ 'common.next' | transloco }}</span>
      </button>
    }

    @if (currentStep === reportIncidentStep.PeopleInvolved) {
      <button type="button" class="btn-cancel" (click)="previousStep()">
        <span>{{ t('back') }}</span>
      </button>
      <button type="button" class="btn-save" (click)="submit()" [disabled]="!isStep2Valid || isSubmitting">
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm"></span>
        } @else {
          <span>{{ t('submit') }}</span>
        }
      </button>
    }
  </div>
</div>